import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, FileDown, Loader2, Mail, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const lastMonths = (from) => {
  const [y, m] = from.split("-").map(Number);
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(y, m - 1 - i, 1);
    return { v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, l: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
  });
};

const Card = ({ title, desc, children, testid, right }) => (
  <section className="rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <div className="flex items-start gap-4 px-5 py-4 border-b border-slate-100">
      <div className="mr-auto"><h2 className="font-semibold">{title}</h2>{desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}</div>
      {right}
    </div>
    <div className="px-5 py-4 space-y-4">{children}</div>
  </section>
);

const Rule = ({ title, desc, on, setOn, testid, children }) => (
  <div className="flex flex-wrap items-center gap-4 py-2 border-b last:border-0 border-slate-100" data-testid={testid}>
    <Switch checked={on} onCheckedChange={setOn} data-testid={`${testid}-switch`} />
    <div className="mr-auto min-w-[220px]"><p className="text-sm font-medium">{title}</p><p className="text-xs text-slate-500">{desc}</p></div>
    {children}
  </div>
);

const Num = ({ value, onChange, suffix, testid, min, max }) => (
  <div className="flex items-center gap-2 text-sm text-slate-600">
    <Input type="number" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      onBlur={() => onChange(Math.min(max, Math.max(min, Number(value) || min)))} className="w-20 h-8" data-testid={testid} />{suffix}
  </div>
);

const Rules = ({ s, set }) => (
  <Card title="Règles d'alerte" desc="Ce qui doit apparaître dans le résumé quotidien." testid="automation-rules">
    <Rule title="Disque presque plein" desc="Un disque dépasse le seuil d'occupation." on={s.disk_enabled} setOn={set("disk_enabled")} testid="rule-disk">
      <Num value={s.disk_threshold} onChange={set("disk_threshold")} min={50} max={99} suffix="% utilisé" testid="rule-disk-threshold" />
    </Rule>
    <Rule title="Pare-feu ou antivirus désactivé" desc="La protection du poste est coupée." on={s.security_enabled} setOn={set("security_enabled")} testid="rule-security" />
    <Rule title="Poste hors ligne" desc="Serveur = type d'appareil ou système contenant « serveur » / « server »." on={s.offline_enabled} setOn={set("offline_enabled")} testid="rule-offline">
      <Num value={s.offline_hours} onChange={set("offline_hours")} min={1} max={720} suffix="h sans contact" testid="rule-offline-hours" />
      <Select value={s.offline_scope} onValueChange={set("offline_scope")}>
        <SelectTrigger className="w-52 h-8" data-testid="rule-offline-scope"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="servers" data-testid="rule-offline-scope-servers">Serveurs uniquement</SelectItem>
          <SelectItem value="all" data-testid="rule-offline-scope-all">Tous les postes</SelectItem>
        </SelectContent>
      </Select>
    </Rule>
    <Rule title="Mises à jour critiques en attente" desc="Une mise à jour système critique n'est pas encore installée." on={s.updates_enabled} setOn={set("updates_enabled")} testid="rule-updates" />
  </Card>
);

const Digest = ({ s, set, emails, setEmails, sendNow, sending }) => (
  <Card title="Résumé quotidien par email" desc="Un seul email par jour avec tous les problèmes en cours. Rien n'est envoyé si tout va bien."
    testid="automation-digest" right={<Switch checked={s.digest_enabled} onCheckedChange={set("digest_enabled")} data-testid="digest-switch" />}>
    <div className="grid sm:grid-cols-[1fr_auto] gap-4">
      <label className="text-sm space-y-1"><span className="text-slate-600">Destinataires (séparés par des virgules)</span>
        <Input value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="digest-emails-input" /></label>
      <label className="text-sm space-y-1"><span className="text-slate-600">Heure d'envoi</span>
        <Select value={String(s.digest_hour)} onValueChange={(v) => set("digest_hour")(Number(v))}>
          <SelectTrigger className="w-32" data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{h} h 00</SelectItem>)}</SelectContent>
        </Select></label>
    </div>
    <Button variant="outline" size="sm" onClick={sendNow} disabled={sending} data-testid="digest-send-now-btn">
      {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Envoyer un résumé maintenant
    </Button>
  </Card>
);

const Preview = ({ items }) => (
  <Card title={`Aperçu du prochain résumé (${items.length})`} desc="Calculé avec les règles enregistrées." testid="automation-preview">
    {!items.length && <p className="text-sm text-emerald-700" data-testid="automation-preview-empty">Aucun problème à signaler pour le moment.</p>}
    <div className="divide-y divide-slate-100">
      {items.map((i) => (
        <div key={`${i.machine_id}-${i.kind}`} className="flex items-start gap-3 py-2 text-sm" data-testid={`preview-${i.machine_id}-${i.kind}`}>
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />
          <p><span className="font-medium">{i.machine}</span>{i.client && <span className="text-slate-500"> · {i.client}</span>} — <span className="text-red-700">{i.label}</span><span className="block text-xs text-slate-500">{i.message}</span></p>
        </div>
      ))}
    </div>
  </Card>
);

const ClientRow = ({ c, month, reload }) => {
  const [email, setEmail] = useState(c.report_email || "");
  const [busy, setBusy] = useState("");
  const saveEmail = () => email !== (c.report_email || "") && api.put(`/automation/clients/${c.group_id}`, { report_email: email || null })
    .then(() => { toast.success("Email du client enregistré"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const pdf = async () => {
    setBusy("pdf");
    try {
      const { data } = await api.get(`/automation/clients/${c.group_id}/report.pdf`, { params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = `rapport-${c.name}-${month}.pdf`; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Téléchargement impossible"); }
    setBusy("");
  };
  const send = () => { setBusy("send"); api.post(`/automation/clients/${c.group_id}/send`, null, { params: { month } })
    .then(({ data }) => { toast.success(`Rapport envoyé à ${data.sent_to}`); reload(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy("")); };
  return (
    <tr className="border-t border-slate-100" data-testid={`client-row-${c.group_id}`}>
      <td className="px-3 py-2"><p className="font-medium">{c.name}</p><p className="text-xs text-slate-500">{c.machines} poste(s)</p></td>
      <td className="px-3"><Input type="email" placeholder="contact@client.fr" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={saveEmail} className="h-8 min-w-[220px]" data-testid={`client-email-${c.group_id}`} /></td>
      <td className="px-3 text-xs text-slate-500">{c.last_report_at ? fmtDate(c.last_report_at) : "Jamais"}</td>
      <td className="px-3 text-right whitespace-nowrap">
        <Button size="sm" variant="outline" onClick={pdf} disabled={!!busy} className="mr-2" data-testid={`client-pdf-${c.group_id}`}>{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />} PDF</Button>
        <Button size="sm" onClick={send} disabled={!!busy || !c.report_email} data-testid={`client-send-${c.group_id}`}>{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />} Envoyer</Button>
      </td>
    </tr>
  );
};

const Reports = ({ s, set }) => {
  const [d, setD] = useState(null);
  const [month, setMonth] = useState("");
  const load = useCallback(() => api.get("/automation/clients").then(({ data }) => { setD(data); setMonth((m) => m || data.previous_month); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card title="Rapports mensuels clients" desc="Le 1er de chaque mois vers 8 h, chaque client avec un email reçoit le bilan du mois écoulé (lien PDF valable 90 jours)."
      testid="automation-reports" right={<Switch checked={s.reports_enabled} onCheckedChange={set("reports_enabled")} data-testid="reports-switch" />}>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <div className="flex items-center gap-2 text-sm text-slate-600">Mois du rapport (téléchargement / envoi manuel) :
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-48 h-8" data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{lastMonths(d.previous_month).map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="clients-table">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="px-3 py-2">Client</th><th className="px-3">Email du rapport</th><th className="px-3">Dernier envoi</th><th /></tr></thead>
            <tbody>
              {!d.clients.length && <tr><td colSpan={4} className="py-6 text-center text-slate-500">Aucun client. Les clients sont les groupes de sessions de la vue Access.</td></tr>}
              {d.clients.map((c) => <ClientRow key={c.group_id} c={c} month={month} reload={load} />)}
            </tbody>
          </table></div>
        </>
      )}
    </Card>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AutomationPanel = () => {
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const apply = (data) => { setS(data); setEmails(data.digest_emails.join(", ")); };
  useEffect(() => { api.get("/automation").then(({ data }) => apply(data)).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const list = emails.split(/[,;\s]+/).filter(Boolean);
    if (!list.length) return toast.error("Indiquez au moins un destinataire pour le résumé");
    const bad = list.find((e) => !EMAIL_RE.test(e));
    if (bad) return toast.error(`Adresse email invalide : ${bad}`);
    setSaving(true);
    try {
      const { preview, ...body } = s;
      const { data } = await api.put("/automation", { ...body, digest_emails: list });
      apply(data); toast.success("Automatisation enregistrée");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const sendNow = () => { setSending(true); api.post("/automation/digest/send")
    .then(({ data }) => toast.success(`Résumé envoyé à ${data.sent_to.join(", ")} (${data.issues} problème(s))`)).catch((e) => toast.error(errMsg(e))).finally(() => setSending(false)); };
  if (!s) return <div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="max-w-5xl space-y-5" data-testid="automation-page">
        <div className="flex flex-wrap items-end gap-4">
          <p className="mr-auto text-sm text-slate-500">Alertes par email et rapports clients envoyés automatiquement.</p>
          <Button onClick={save} disabled={saving} data-testid="automation-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Enregistrer</Button>
        </div>
        <Digest s={s} set={set} emails={emails} setEmails={setEmails} sendNow={sendNow} sending={sending} />
        <Rules s={s} set={set} />
        <Preview items={s.preview} />
        <Reports s={s} set={set} />
    </div>
  );
};
